import { Box, useMantineTheme } from '@mantine/core';
import { keyframes } from '../utils/keyframes';;
import { forwardRef } from 'react';
import { useReducedMotion } from '../hooks';

const shimmerKeyframes = keyframes({
  '0%': { backgroundPosition: '-200% 0' },
  '100%': { backgroundPosition: '200% 0' },
});

export interface ShimmerProps {
  children?: React.ReactNode;
  width?: number | string;
  height?: number | string;
  radius?: number | string;
  duration?: number;
  baseColor?: string;
  highlightColor?: string;
  active?: boolean;
}

const Shimmer = forwardRef<HTMLDivElement, ShimmerProps>(
  (
    {
      children,
      width = '100%',
      height = 20,
      radius = 'sm',
      duration = 1500,
      baseColor,
      highlightColor,
      active = true,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const reducedMotion = useReducedMotion();

    const base = baseColor || theme.colors.gray[2];
    const highlight = highlightColor || theme.colors.gray[0];

    if (!active) {
      return <Box ref={ref}>{children}</Box>;
    }

    return (
      <Box
        ref={ref}
        style={{
          width,
          height,
          borderRadius: theme.radius[radius as keyof typeof theme.radius] || radius,
          background: reducedMotion
            ? base
            : `linear-gradient(90deg, ${base} 0%, ${highlight} 50%, ${base} 100%)`,
          backgroundSize: '200% 100%',
          animation: reducedMotion ? 'none' : `${shimmerKeyframes} ${duration}ms ease-in-out infinite`,
        }}
      >
        {children}
      </Box>
    );
  }
);

Shimmer.displayName = 'Shimmer';

export default Shimmer;
